import MyCardsCollection from '../../components/MyCardsCollection';
import {
  useAddCardToHaveListMutation,
  useCardToHaveListQuantityUpdateMutation,
  useHaveListFetchPaginatedQuery,
  useRemoveCardFromHaveListMutation,
  useUpdateHaveListCardMutation,
  useDeleteAllHaveListItemsMutation,
  useToggleTradeStatusMutation,
  useBatchUpdateHaveListMutation,
  useLazyExportHaveListQuery,
  UpdateHaveListCardRequest,
} from 'store/services/collectionList/haveList/haveListApi';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  generateHaveListCsv,
  downloadCsv,
  HaveListExportItem,
} from '../../utils/collectionCsvExport';
import './HaveList.css';
import { SortOrder } from 'types/interfaces/sort';
import { useSortPreference } from 'hooks/useSortPreference';
import { CollectionList } from 'types/interfaces/collectionList';
import EditHaveListCardModal from 'components/EditHaveListCardModal';
import DeleteHaveListModal from 'components/DeleteHaveListModal';
import { toast } from 'react-toastify';

const HAVE_LIST_ADD_CARDS_PLACEHOLDER = 'Card name';

export const HaveList = () => {
  const { orderBy, setOrderBy } = useSortPreference('haveListSort', 'created_at', 'desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<'list' | 'comfortable' | 'compact'>(() => {
    // Load saved view mode from localStorage
    const saved = localStorage.getItem('haveListViewMode');
    return (saved as 'list' | 'comfortable' | 'compact') || 'list';
  });
  const [editingCard, setEditingCard] = useState<CollectionList | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Trade filter state
  const [tradeFilter, setTradeFilter] = useState<'all' | 'forTrade' | 'notForTrade'>('all');

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounce search term (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset pagination when search changes
  useEffect(() => {
    if (debouncedSearchTerm) {
      setCurrentPage(1);
    }
  }, [debouncedSearchTerm]);

  // Clear search on unmount (tab switch)
  useEffect(() => {
    return () => {
      setSearchTerm('');
      setIsSearchOpen(false);
    };
  }, []);

  // Convert trade filter to query param
  const forTradeParam = tradeFilter === 'forTrade' ? true : tradeFilter === 'notForTrade' ? false : undefined;

  const {
    data: haveListData,
    isFetching: haveListLoading,
  } = useHaveListFetchPaginatedQuery({
    page: currentPage,
    ...orderBy,
    search: debouncedSearchTerm || undefined, // Don't send empty string
    forTrade: forTradeParam,
  });

  const [addCard] = useAddCardToHaveListMutation();
  const [removeCard] = useRemoveCardFromHaveListMutation();
  const [haveListCardQuantityChange] = useCardToHaveListQuantityUpdateMutation();
  const [updateCard] = useUpdateHaveListCardMutation();
  const [toggleTradeStatus] = useToggleTradeStatusMutation();
  const [deleteAllHaveListItems, { isLoading: isDeleting }] = useDeleteAllHaveListItemsMutation();
  const [batchUpdate] = useBatchUpdateHaveListMutation();
  const [triggerExport] = useLazyExportHaveListQuery();
  const [isExporting, setIsExporting] = useState(false);

  const allHaveListCards = useMemo(
    () => haveListData?.haveList || [],
    [haveListData?.haveList]
  );

  const onCardAdd = async (identifier: string | number, cardData?: any) => {
    // For have list, we expect cardId (number) with specific printing
    if (typeof identifier === 'number') {
      try {
        // Determine foil status from card data
        // Backend treats etched cards as foil (foil: true)
        const price = cardData?.price?.[0] || cardData?.price;
        const hasNonFoil = price?.usd != null && Number(price.usd) > 0;
        const hasFoil = price?.usdFoil != null && Number(price.usdFoil) > 0;
        const hasEtched = price?.usdEtched != null && Number(price.usdEtched) > 0;

        // If card has non-foil available, default to non-foil
        // Otherwise if foil or etched exists, add as foil
        const shouldBeFoil = !hasNonFoil && (hasFoil || hasEtched);

        await addCard({
          cardId: identifier,
          quantity: 1,
          condition: 'NEAR_MINT' as any,
          foil: shouldBeFoil,
        }).unwrap();
      } catch (error) {
        console.error('Failed to add card to have list:', error);
        toast.error('Failed to add card. Please try again.');
      }
    } else {
      console.error('Have list requires cardId (number), but received:', typeof identifier);
    }
  };

  const onCardRemove = (identifier: string | number) => {
    // For have list, identifier is the row.id (UserMTGCardHave record ID)
    // The API expects haveListRecordId (the UserMTGCardHave.id)
    if (typeof identifier === 'number') {
      removeCard({ haveListRecordId: identifier, ...orderBy });
    } else {
      console.error('Invalid identifier for have list remove:', identifier);
    }
  };

  const onHaveCardQuantityChange = async (identifier: string | number, change: number) => {
    console.log('onHaveCardQuantityChange called:', { identifier, change, orderBy });

    if (typeof identifier === 'number') {
      try {
        // identifier is the UserMTGCardHave record ID (row.id)
        // The backend expects this as haveListRecordId
        await haveListCardQuantityChange({ haveListRecordId: identifier, change, ...orderBy }).unwrap();
        console.log('Quantity update successful');
      } catch (error) {
        console.error('Quantity update failed:', error);
        toast.error('Failed to update quantity. Please try again.');
      }
    } else {
      console.error('Invalid identifier type for have list quantity change:', typeof identifier);
    }
  };

  const onHeaderSortPress = (key: string, explicitDirection?: SortOrder) => {
    if (explicitDirection) {
      setOrderBy({ sort: key, direction: explicitDirection });
    } else if (key === orderBy.sort) {
      setOrderBy((prev) => ({
        sort: key,
        direction: prev.direction === 'asc' ? 'desc' : 'asc',
      }));
    } else {
      // Alert column starts descending so alert-enabled cards sort to the top on first click.
      setOrderBy({
        direction: key === 'alert_enabled' ? 'desc' : 'asc',
        sort: key,
      });
    }
    // Reset to first page when sorting changes
    setCurrentPage(1);
  };

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
    // Scroll to top of cards container so user sees the new page content
    setTimeout(() => {
      document.querySelector('.collection-cards-container')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 0);
  }, []);

  const handleCardEdit = (card: CollectionList) => {
    console.log('Opening edit modal for card:', card);
    setEditingCard(card);
  };

  const handleSaveEdit = async (updates: UpdateHaveListCardRequest) => {
    try {
      await updateCard(updates).unwrap();
      toast.success('Card updated successfully');
      setEditingCard(null);
    } catch (error) {
      console.error('Failed to update card:', error);
      toast.error('Failed to update card. Please try again.');
      throw error; // Re-throw so modal can handle it
    }
  };

  const handleDeleteAll = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const result = await deleteAllHaveListItems().unwrap();
      toast.success(`Successfully deleted ${result.deletedCount} cards from your collection`);
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Failed to delete have list:', error);
      toast.error('Failed to delete collection. Please try again.');
    }
  };

  const handleTradeStatusToggle = useCallback(async (haveListRecordId: number, forTrade: boolean) => {
    try {
      await toggleTradeStatus({ haveListRecordId, forTrade }).unwrap();
    } catch (error) {
      console.error('Failed to toggle trade status:', error);
      toast.error('Failed to update trade status. Please try again.');
    }
  }, [toggleTradeStatus]);

  const handleBatchUpdate = useCallback(async (ids: number[], changes: { condition?: any; foil?: boolean; forTrade?: boolean }) => {
    const result = await batchUpdate({ ids, changes }).unwrap();
    return result;
  }, [batchUpdate]);

  const handleTradeFilterChange = useCallback((filter: 'all' | 'forTrade' | 'notForTrade') => {
    setTradeFilter(filter);
    setCurrentPage(1);
  }, []);

  const handleViewModeChange = useCallback((mode: 'list' | 'comfortable' | 'compact') => {
    setViewMode(mode);
    // Save preference to localStorage
    localStorage.setItem('haveListViewMode', mode);
  }, []);

  const handleExportCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      const result = await triggerExport({
        search: debouncedSearchTerm || undefined,
        forTrade: forTradeParam,
      }).unwrap();

      if (!result.items || result.items.length === 0) {
        toast.info('No cards to export.');
        return;
      }

      const csvContent = generateHaveListCsv(result.items as HaveListExportItem[]);
      const date = new Date().toISOString().split('T')[0];
      downloadCsv(csvContent, `trademagic-have-list-${date}.csv`);

      if (result.truncated) {
        toast.warning('Export limited to 10,000 rows. Some cards were not included.');
      } else {
        toast.success(`Exported ${result.items.length} cards.`);
      }
    } catch (error) {
      console.error('Failed to export have list:', error);
      toast.error('Failed to export. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [triggerExport, debouncedSearchTerm, forTradeParam]);

  // Calculate total pages based on 100 records per page
  const totalPages = haveListData ? Math.ceil(haveListData.totalRecords / 100) : 1;

  return (
    <div className="have-list">
      <MyCardsCollection
        isLoading={haveListLoading}
        data={allHaveListCards}
        order={orderBy.direction}
        orderBy={orderBy.sort}
        addCardsPlaceholder={HAVE_LIST_ADD_CARDS_PLACEHOLDER}
        addCardsTitle="Add cards to your Have List"
        addCardsSubtitle="These are cards in your collection. We'll use this list to auto-match you with other traders on the send page."
        onHeaderSortPress={onHeaderSortPress}
        onCardAdd={onCardAdd}
        cardQuantityChange={onHaveCardQuantityChange}
        onCardDelete={onCardRemove}
        onCardEdit={handleCardEdit}
        useOracleId={false}
        isHaveList={true}
        onDeleteAll={handleDeleteAll}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={haveListData?.totalCards || 0}
        totalRecords={haveListData?.totalRecords || 0}
        onPageChange={handlePageChange}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        isSearchOpen={isSearchOpen}
        onSearchToggle={() => setIsSearchOpen(!isSearchOpen)}
        filteredCount={haveListData?.filteredCount}
        onTradeStatusToggle={handleTradeStatusToggle}
        tradeFilter={tradeFilter}
        onTradeFilterChange={handleTradeFilterChange}
        onBatchUpdateHaveList={handleBatchUpdate}
        onExportCsv={handleExportCsv}
        isExporting={isExporting}
      />

      <EditHaveListCardModal
        isOpen={!!editingCard}
        card={editingCard}
        onClose={() => setEditingCard(null)}
        onSave={handleSaveEdit}
      />

      <DeleteHaveListModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};
